import { useMounted } from "@/features/open-home/utils/use-mounted";
import { useFeatureGate } from "@statsig/react-bindings";

/**
 * Open Home is invisible until Thomas switches it on in Statsig (decision 10-03).
 *
 * - `open_home`: every entry point (main menu, search tab, account menu, dashboard) and the
 *   `/open-home/*` pages and API.
 * - `open_home_home_promo`: the home page promo block, separately, default off.
 * - `open_home_search_tab`: the "For sale" | "Open Home" switch above search results and PLPs,
 *   separately, default off (also needs `open_home`).
 * - `open_home_public`: the public ways in, the header menu entry under "New" and the home page
 *   promo (on top of their own gates), default off. Off while the /open-home link goes round
 *   friends and family for feedback (Thomas, 10-10): "New" is a plain link to the new items again.
 *
 * Unknown or failed gates read as off. `OPEN_HOME_FORCE_ENABLED=1` (Vercel preview env for the
 * branch) forces both on for testing; next.config.js exposes it to the browser.
 */
export const OPEN_HOME_GATE = "open_home";
export const OPEN_HOME_PROMO_GATE = "open_home_home_promo";
export const OPEN_HOME_SEARCH_TAB_GATE = "open_home_search_tab";
export const OPEN_HOME_PUBLIC_GATE = "open_home_public";

export type OpenHomeGateName =
  | typeof OPEN_HOME_GATE
  | typeof OPEN_HOME_PROMO_GATE
  | typeof OPEN_HOME_SEARCH_TAB_GATE
  | typeof OPEN_HOME_PUBLIC_GATE;

export const openHomeForced = () =>
  process.env.OPEN_HOME_FORCE_ENABLED === "1" ||
  process.env.NEXT_PUBLIC_OPEN_HOME_FORCE_ENABLED === "1";

/** Client side: true only when the gate is on (or forced). Reading it logs no exposure. */
export const useOpenHomeGate = (gate: OpenHomeGateName = OPEN_HOME_GATE) => {
  const result = useFeatureGate(gate, { disableExposureLog: true });
  // The server render has no Statsig values: gated UI appears only after mount, so both renders
  // agree (React #418 otherwise).
  const mounted = useMounted();

  return mounted && (openHomeForced() || Boolean(result?.value));
};
